import React from "react";
import PropTypes from "prop-types";
import { Add } from "@mui/icons-material";
import { Typography } from "@mui/material";

import { UIButton, TabSetsLevels } from "../../../../../ui";
import ShiftPanel from "../ShiftPanel";
import DuplicateTrainingFromShift from "../DuplicateTrainingFromShift";
import { useSelector } from "react-redux";
import messages from "../../../../../../assets/locale/messages";
import {
  getShiftEditLocks,
  getShiftOptionLabel,
  hasAvailableShiftType,
} from "../helpers";
import { clx } from "../../../../../../utils/Helpers";

const ShiftConfiguration = ({
  formik,
  role,
  roleIndex,
  activeShiftIndex,
  onShiftChange,
  onAddShift,
  onRemoveShift,
  onTrainingToggle,
  defaultTrainingBoundaryMeters,
  hasError,
  portalCompanyOngoingEdit,
  companyPortalMode,
  isEditMode,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const t = events?.creatEvent?.defineRolesAndShifts;
  
  const activeShift = role.event_role_shifts_attributes?.[activeShiftIndex];
  const canAddShift = hasAvailableShiftType(role.event_role_shifts_attributes);
  const activeShiftLocks = activeShift
    ? getShiftEditLocks({
        formik,
        shift: activeShift,
        shiftBaseName: `event_roles_attributes.${roleIndex}.event_role_shifts_attributes.${activeShiftIndex}`,
        portalCompanyOngoingEdit,
        companyPortalMode,
        isEditMode,
      })
    : {};

  return (
    <div className={clx("shift-section", { "shift-section--error": hasError })}>
      <div className="shift-section__title">
        <Typography className="text-label-md" component="span">
          {t.shiftConfiguration}
        </Typography>
      </div>

      <div className="shift-section__tabs-row">
        <TabSetsLevels
          tabs={role.event_role_shifts_attributes.map((shift, index) => {
            const shiftLabel = getShiftOptionLabel(shared, shift.shift_type);

            return {
              label: shiftLabel || t.shiftLabel.replace("{number}", index + 1),
              value: index,
            };
          })}
          value={activeShiftIndex}
          onChange={onShiftChange}
        />
        {canAddShift && (
          <UIButton
            className="shift-section__add"
            startIcon={<Add />}
            variant="outlined"
            onClick={onAddShift}
            type="button"
            color="inherit"
          >
            {t.addShift}
          </UIButton>
        )}
      </div>

      {activeShift && (
        <DuplicateTrainingFromShift
          formik={formik}
          roleIndex={roleIndex}
          shiftIndex={activeShiftIndex}
          shifts={role.event_role_shifts_attributes}
          disabled={
            activeShiftLocks.organizerShiftLocked ||
            activeShiftLocks.trainingScheduleLocked
          }
        />
      )}

      {activeShift && (
        <ShiftPanel
          formik={formik}
          role={role}
          roleIndex={roleIndex}
          shift={activeShift}
          shiftIndex={activeShiftIndex}
          onRemoveShift={onRemoveShift}
          onTrainingToggle={onTrainingToggle}
          defaultTrainingBoundaryMeters={defaultTrainingBoundaryMeters}
          hasError={hasError}
          portalCompanyOngoingEdit={portalCompanyOngoingEdit}
          companyPortalMode={companyPortalMode}
          isEditMode={isEditMode}
        />
      )}
    </div>
  );
};

ShiftConfiguration.propTypes = {
  formik: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired,
  roleIndex: PropTypes.number.isRequired,
  activeShiftIndex: PropTypes.number.isRequired,
  onShiftChange: PropTypes.func.isRequired,
  onAddShift: PropTypes.func.isRequired,
  onRemoveShift: PropTypes.func.isRequired,
  onTrainingToggle: PropTypes.func.isRequired,
  defaultTrainingBoundaryMeters: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  hasError: PropTypes.bool,
  portalCompanyOngoingEdit: PropTypes.bool,
  companyPortalMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

ShiftConfiguration.defaultProps = {
  hasError: false,
  portalCompanyOngoingEdit: false,
  companyPortalMode: false,
  isEditMode: false,
};

export default ShiftConfiguration;
